import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Alert, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  useListUsersQuery,
  useResendInvitationMutation,
  User,
} from '../../store/api/userApi';
import { hasActiveFilters } from '@/utils/helpers';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import { TanStackTableViewWithSchema } from '@/components/common/Table';
import { PermissionButton } from '../common/PermissionButton';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import { useToast } from '../common/Toast/ToastProvider';
import { parseApiError } from '@/utils/apiFieldErrors';
import usePermissions from '@/hooks/usePermissions';


const UserList: React.FC = () => {
  const navigate = useNavigate();

  // Complete pagination navigation hook
  const {
    page,
    rowsPerPage,
    searchTerm,
    sortKey,
    sortDirection,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    handleFilterChange,
    clearFilters,
    toDetail,
    toEdit,
    toCreate,
  } = useCompletePaginationNavigation(navigate, '/users');

  const handleClearAllFilters = () => {
    // Clear URL filters - this will automatically update the UI
    clearFilters();
  };

  const queryParams = useMemo(() => {
    const apiParams: Record<string, any> = {
      page: page + 1,
      limit: rowsPerPage,
      cf: true,
    };

    if (searchTerm) apiParams.search = searchTerm;

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') apiParams[key] = value;
    });

    if (sortKey) {
      apiParams.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
    }
    
    return apiParams;
  }, [page, rowsPerPage, searchTerm, filters, sortKey, sortDirection]);

  const { data, error, isLoading, isFetching } =
    useListUsersQuery(queryParams);

  const users = data?.results || [];
  const totalCount = data?.count || 0;

  const { showToast } = useToast();
  const { canAdd } = usePermissions();
  const [resendInvitation] = useResendInvitationMutation();
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const handleViewUser = (id: string | number) => toDetail(id);
  const handleEditUser = (id: string | number) => toEdit(id);
  const handleAddUser = () => toCreate();

  const handleResendInvitation = async (userId: string) => {
    setResendingUserId(userId);
    try {
      await resendInvitation(userId).unwrap();
      showToast('Invitation resent successfully', 'success');
    } catch (err) {
      if (typeof err === 'object' && err !== null && (err as { status?: number }).status === 429) {
        showToast('Too many invitation requests. Please try again later.', 'error');
      } else {
        const { message } = parseApiError(err);
        showToast(message || 'Failed to resend invitation', 'error');
      }
    } finally {
      setResendingUserId(null);
    }
  };

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">User List</h3>
      <div className="appGridContainer">
        <div className="gridHeader">
          <div className="gridHeadLeft">
            <div className="inlineFormContainer">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleClearAllFilters}
                size="small"
                sx={{ ml: 2, height: 40 }}
                startIcon={<FilterListIcon />}
                disabled={!hasActiveFilters(filters)}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          <div className="gridHeadRight">
            <PermissionButton
              module="user"
              permissionAction="add"
              startIcon={<Add />}
              color="primary"
              onClick={() => navigate('../users/invite')}
              className="button button__solid--primary"
            >
              Add User
            </PermissionButton>
          </div>
        </div>
        <div className="gridWrapper">
          {error ? (
            <Alert severity="error">
              Error loading users. Please try again later.
            </Alert>
          ) : (
            <>
              <TanStackTableViewWithSchema
                data={users}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                handleView={(id: string | number) => handleViewUser(String(id))}
                handleEdit={(id: string | number) => handleEditUser(String(id))}
                loading={isFetching || isLoading}
                permissionModule="user"
                entity="user"
                enableAPIConfig={true}
                onFilterChange={handleFilterChange}
                filterValues={filters}
                fallbackHeaders={[]} // No fallback headers - rely entirely on schema
                renderRowActions={(row: User) => {
                  const canResend = Boolean(row.invitation_sent_at) && !row.invitation_accepted_at;
                  if (!canResend || !canAdd('user')) return null;

                  const isResendingThisRow = resendingUserId === row.id;
                  return (
                    <Tooltip title="Resend Invitation">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={isResendingThisRow}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvitation(row.id);
                          }}
                        >
                          {isResendingThisRow ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SendIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserList;